import { useMutation, useQuery } from "@tanstack/react-query";
import { notification } from "antd";
import { QUERY_NAME, SERVICES } from "@lhd/constants";
import { getCompanyId } from "@lhd/utility";
import axios from "@lhd/libs/axios";

export const useFetchApplication = () => {
  return useQuery(
    [QUERY_NAME.APPLICATIONS],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.APPLICATIONS}`,
      }).then((res) => res.data),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: true,
    },
  );
};

export const useFetchCompanyApplication = (company_id: any) => {
  return useQuery(
    [QUERY_NAME.APPLICATIONS, company_id],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.APPLICATIONS}?company_id=${company_id}`,
      }).then((res) => res.data),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: !!company_id,
    },
  );
};

export const useFetchPaymentHistory = (company_id: any, yearMonth: string) => {
  return useQuery(
    [QUERY_NAME.APPLICATIONS, company_id, yearMonth],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.PAYMENT_HISTORY}?company_id=${company_id}&year_month=${yearMonth}`,
      }).then((res) => res.data),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: !!company_id,
    },
  );
};
export const useFetchPurchaseStatus = (company_id: any) => {
  return useQuery(
    ["check-purchase-status", company_id],
    () =>
      axios({
        method: "get",
        url: `payment_histories/check-purchase-status?company_id=${company_id}`,
      }).then((res) => res.data),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: !!company_id,
    },
  );
};

export const useUpdateAppEstimates = ({ onSuccess }: any) => {
  return useMutation(
    ({ payload }: any) =>
      axios({
        method: "post",
        url: `${SERVICES.COMPANY_APP_ESTIMATES}`,
        data: payload,
        params: {
          company_id: getCompanyId(),
        },
      }),
    {
      onSuccess() {
        onSuccess?.();
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useCreateSubscription = ({ onSuccess }: any) => {
  return useMutation(
    ({ payload }: any) =>
      axios({
        method: "post",
        url: `${SERVICES.CREATE_SUBSCRIPTION}`,
        data: payload,
        params: {
          company_id: getCompanyId(),
        },
      }).then((res) => res.data),
    {
      onSuccess(res) {
        onSuccess?.(res);
        // notification["success"]({
        //   message: "保存しました",
        // });
      },
    },
  );
};

export const useFetchStaffList = (
  page: number,
  pageSize: number,
  filterParams: any,
) => {
  const companyId = getCompanyId();
  const { __BLOCK_API__, ...restFilterParams } = filterParams || {};

  return useQuery(
    [QUERY_NAME.CLIENTS, companyId, page, pageSize, filterParams],
    () =>
      axios({
        method: "get",
        url: SERVICES.SUBSCRIPTION,
        params: {
          company_id: companyId,
          page,
          per_page: pageSize,
          ...restFilterParams,
        },
      }).then((res) => ({
        data: res.data,
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: 3000,
      staleTime: 3000,
      enabled: !__BLOCK_API__,
    },
  );
};

export const useUpdateStaffSetting = ({ onSuccess }: any) => {
  return useMutation(
    ({ payload }: any) =>
      axios({
        method: "post",
        url: SERVICES.SUBSCRIPTION,
        data: payload,
        params: {
          company_id: getCompanyId(),
        },
      }),
    {
      onSuccess() {
        onSuccess?.();
        notification["success"]({
          message: "保存しました",
        });
      },
      onError({ response }: any) {
        notification["error"]({
          message: response?.data?.msg || response?.data?.error,
        });
      },
    },
  );
};

export interface SubscriptionFilterOptionsParams {
  company_id: number;
  field_key: string;
  active_filters?: Record<string, string[]>;
  filter_order?: string[];
  active_search?: string;
}

export interface SubscriptionFilterOption {
  value: string;
  label: string;
  count: number;
}

export interface SubscriptionFilterOptionsResponse {
  success: boolean;
  filter_options: Record<string, SubscriptionFilterOption[]>;
}

export const useFetchSubscriptionFilterOptions = (
  params: SubscriptionFilterOptionsParams | null,
  enabled = true,
) => {
  return useQuery(
    [
      "subscription_filter_options",
      params?.company_id,
      params?.field_key,
      params?.active_filters,
      params?.filter_order,
      params?.active_search,
    ],
    async () => {
      if (!params) return null;

      const res = await axios({
        method: "post",
        url: `${SERVICES.SUBSCRIPTION}/filter_options`,
        data: {
          company_id: params.company_id,
          field_key: params.field_key,
          active_filters: params.active_filters || {},
          filter_order: params.filter_order || [],
          ...(params.active_search && { active_search: params.active_search }),
        },
      });

      return res.data as SubscriptionFilterOptionsResponse;
    },
    {
      enabled: !!params && enabled,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      retry: false,
      cacheTime: 3000,
      staleTime: 3000,
    },
  );
};
